import styled from "@emotion/native"
import { useEffect, useMemo, useRef, useState } from "react"
import { Keyboard, SectionList, useWindowDimensions } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { TrueSheet } from "../../shims/react-native-true-sheet"
import { CountryButton, Spacing, Text } from "../../ui-components"
import { CountryPickerSearch } from "./CountryPickerSearch"
import { PopularCountriesSection } from "./PopularCountriesSection"
import { allCountryCodes } from "./utils"
import type { CountryItem, CountryPickerProps } from "./types"
import {
  filterCountriesBySearch,
  filterExcludedCountries,
  getCountryName,
  getPopularCountries,
  groupCountriesByContinent,
  sortCountriesByName,
  type CountryListSection,
} from "./utils"

const DEFAULT_EXCLUDED = ["PS"]
const DEFAULT_POPULAR = ["US", "CA"]
const SHEET_HEIGHT_FRACTION = 0.85

export function CountryPickerModal({
  visible,
  onClose,
  onSelect,
  lang = "en",
  excludedCountryCodes = DEFAULT_EXCLUDED,
  popularCountryCodes = DEFAULT_POPULAR,
}: CountryPickerProps) {
  const sheetRef = useRef<TrueSheet>(null)
  const { height: windowHeight } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const [searchValue, setSearchValue] = useState("")

  const detentFraction = useMemo(() => {
    const leaveTopClearance = insets.top + 56
    const sheetHeight = Math.floor(
      Math.min(windowHeight * SHEET_HEIGHT_FRACTION, windowHeight - leaveTopClearance)
    )
    const available = Math.max(1, windowHeight - insets.top)
    return Math.min(0.92, Math.max(0.5, sheetHeight / available))
  }, [insets.top, windowHeight])

  useEffect(() => {
    if (visible) {
      void sheetRef.current?.present()
      return
    }

    void sheetRef.current?.dismiss()
  }, [visible])

  const countries = useMemo(
    () => filterExcludedCountries(allCountryCodes, excludedCountryCodes),
    [excludedCountryCodes]
  )

  const popularCountries = useMemo(
    () => getPopularCountries(countries, popularCountryCodes),
    [countries, popularCountryCodes]
  )

  const isSearching = searchValue.trim().length > 0

  const filteredCountries = useMemo(
    () => filterCountriesBySearch(countries, searchValue, lang),
    [countries, searchValue, lang]
  )

  const sections: CountryListSection[] = useMemo(() => {
    if (isSearching) {
      return [
        {
          continent: "asia",
          title: "",
          data: sortCountriesByName(filteredCountries, lang),
        },
      ]
    }
    return groupCountriesByContinent(countries, lang)
  }, [countries, filteredCountries, isSearching, lang])

  const showPopularSection = !isSearching

  const handleDidDismiss = () => {
    setSearchValue("")
    onClose()
  }

  const handleSelect = (country: CountryItem) => {
    Keyboard.dismiss()
    onSelect(country)
    void sheetRef.current?.dismiss()
  }

  const sheetHeader = useMemo(
    () => (
      <Header>
        <Text type="h3" color="primary">
          Country code
        </Text>
        <Spacing top={16} />
        <CountryPickerSearch
          value={searchValue}
          onChangeText={setSearchValue}
          placeholder="Search country or dial code"
          autoCorrect={false}
          autoCapitalize="none"
          testID="countryCodesPickerSearchInput"
        />
      </Header>
    ),
    [searchValue]
  )

  return (
    <TrueSheet
      ref={sheetRef}
      detents={[detentFraction]}
      dimmed
      dimmedDetentIndex={0}
      dismissible
      draggable
      grabber
      cornerRadius={36}
      scrollable
      maxContentWidth={520}
      onDidDismiss={handleDidDismiss}
      header={sheetHeader}
      headerStyle={{ paddingTop: 16, paddingHorizontal: 16 }}
      backgroundColor="white"
    >
      <SheetChrome>
        <SectionList
          sections={sections}
          keyExtractor={(item) => item.code}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          testID="countryCodesPickerFlatList"
          style={{ flex: 1, minHeight: 0 }}
          contentContainerStyle={{ paddingBottom: 24 }}
          stickySectionHeadersEnabled={false}
          ListHeaderComponent={
            showPopularSection ? (
              <PopularCountriesSection
                countries={popularCountries}
                lang={lang}
                onSelect={handleSelect}
              />
            ) : (
              () => null
            )
          }
          ListEmptyComponent={
            <EmptyState>
              <Text type="body2" color="tertiary">
                No countries match your search
              </Text>
            </EmptyState>
          }
          renderSectionHeader={({ section }) =>
            section.title ? (
              <ContinentHeader>
                <Text type="label2" color="tertiary3">
                  {section.title}
                </Text>
              </ContinentHeader>
            ) : null
          }
          renderItem={({ item }) => (
            <CountryButton
              item={item}
              name={getCountryName(item, lang)}
              onPress={() => handleSelect(item)}
            />
          )}
        />
      </SheetChrome>
    </TrueSheet>
  )
}

const SheetChrome = styled.View`
  margin: 0 16px;
  flex: 1;
`

const Header = styled.View`
  padding-bottom: 12px;
`

const ContinentHeader = styled.View`
  padding-top: 12px;
  padding-bottom: 8px;
`

const EmptyState = styled.View`
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
`
